import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { Users, CalendarDays, Star, PoundSterling, LogOut } from "lucide-react";
import { LOGO_URL } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/admin", end: true, label: "Client Tracker", Icon: Users, slug: "clients" },
  { to: "/admin/calendar", label: "Booking Calendar", Icon: CalendarDays, slug: "calendar" },
  { to: "/admin/revenue", label: "Revenue", Icon: PoundSterling, slug: "revenue" },
  { to: "/admin/reviews", label: "Review Tracker", Icon: Star, slug: "reviews" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100" data-testid="admin-loading">
        <div className="h-8 w-8 border-2 border-[#1A237E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex admin-scope" data-testid="admin-dashboard">
      <aside className="w-64 shrink-0 bg-[#1A237E] text-slate-300 flex flex-col min-h-screen sticky top-0">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <img src={LOGO_URL} alt="Mobile Kitchen Company logo" className="h-10 w-auto rounded bg-white p-1" data-testid="admin-sidebar-logo" />
          <div className="leading-tight">
            <p className="text-white text-sm font-semibold">Mobile Kitchen Co.</p>
            <p className="text-xs text-slate-400">Admin Dashboard</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1" data-testid="admin-sidebar-nav">
          {LINKS.map(({ to, end, label, Icon, slug }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${slug}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-[#1E3A8A] text-white" : "hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium w-full hover:bg-white/5 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <p className="text-sm text-slate-500">
            Signed in as <span className="font-medium text-slate-800">{user.email}</span>
          </p>
          <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#1A237E] hover:underline" data-testid="view-public-site-link">
            View public site
          </a>
        </header>
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
